"use client";

import React, { useEffect, useState } from "react";
import useTemplateBase from "./TemplateBase";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Home,
  Users,
  Eye,
  Utensils,
  X,
  Plus,
  ImageIcon,
  Tag,
  Check,
  Loader2,
  Globe,
  Building2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import profile from "../../../public/profilePicture/profile.webp";
import openHouseBadge from "../../assets/star_red.png";

interface LoggedUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  image?: string;
  avatar?: string;
  company?: string;
  company_logo?: string;
}

interface Property {
  id: number;
  propertyListType?: { id: number; list_name: string; custom_template_data?: Record<string, any> };
  title: string;
  listing_id?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  photos?: string[] | null;
  is_open_house?: boolean;
}

interface PropertyList {
  id: number;
  list_name: string;
}

interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

interface Template10Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Partial<any>) => void;
  onPropertyListChange?: (selectedList: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  selectedList?: string;
  readOnly?: boolean;
}

interface FeatureItem {
  id?: number;
  feature_name: string;
}

const Template10: React.FC<Template10Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  selectedList: viewSelectedList,
  onPropertyListCreated,
  readOnly = false,
}) => {
  const { toast } = useToast();

  const [isCustomList, setIsCustomList] = useState(false);
  const [customListName, setCustomListName] = useState("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] = useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<string | null>(null);

  const secondaryImageRef = React.useRef<HTMLInputElement>(null);
  const tertiaryImageRef = React.useRef<HTMLInputElement>(null);

  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePropertyList,
    updatePhotoAtIndex,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  useEffect(() => {
    setLocalSelectedListOverride(null);
  }, [property?.id]);

  const [website, setWebsite] = useState("www.realtygreatsite.com");

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Image must be less than 10 MB", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setFlyerData((prev) => ({ ...prev, heroImage: result }));
      onDirtyChange?.(true);
      onChange?.({ heroImage: result });
    };
    reader.readAsDataURL(file);
  };

  const handleSecondaryImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    slot: "photo2" | "photo3"
  ) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Image must be less than 10 MB", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      setFlyerData((prev) => ({ ...prev, [slot]: dataUrl }));

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        const uploadRes = await axiosInstance.post(endpoints.mls_uploads, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "multipart/form-data",
          },
        });

        if (uploadRes?.data?.url) {
          await updatePhotoAtIndex(uploadRes.data.url, slot);
          toast({ title: "Success", description: "Photo uploaded successfully", duration: 1000 });
        }
      } catch (err) {
        console.error("Failed to upload image:", err);
        toast({ title: "Error", description: "Failed to upload photo", variant: "destructive" });
      }
    };
    reader.readAsDataURL(file);
  };

  const selectedList =
    localSelectedListOverride ??
    viewSelectedList ??
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "Modern";

  const allLists =
    localPropertyLists.length > 0
      ? localPropertyLists
      : [
        { id: 1, list_name: "Modern" },
        { id: 2, list_name: "Luxury" },
        { id: 3, list_name: "Contemporary" },
        { id: 4, list_name: "Classic" },
      ];

  const saveCustomList = async () => {
    if (readOnly) return;
    const trimmedName = customListName.trim();

    if (!trimmedName) {
      toast({ title: "Error", description: "Please enter a name for your custom tag", variant: "destructive" });
      return;
    }
    if (!property?.id) {
      toast({ title: "Error", description: "No property selected", variant: "destructive" });
      return;
    }

    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");
      toast({ title: "List Selected", description: `"${existingList.list_name}" selected` });
      return;
    }

    setIsSavingCustomList(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );

      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };

      setLocalSelectedListOverride(newListType.list_name);
      setLocalPropertyLists((prev) => [...prev, newListType]);
      setIsCustomList(false);
      setCustomListName("");
      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);

      toast({ title: "Custom Tag Created", description: `"${newListType.list_name}" created` });
    } catch (err: any) {
      console.error("Failed to create custom list:", err);
      toast({ title: "Error", description: "Failed to create custom tag", variant: "destructive" });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  const handleListSelection = (value: string) => {
    if (readOnly) return;

    if (value === "__custom__") {
      setIsCustomList(true);
      setCustomListName("");
      return;
    }

    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  const featureIcons = [Home, Users, Utensils, Eye];

  const images = [
    flyerData.heroImage || "",
    flyerData.photo2 || "",
    flyerData.photo3 || "",
  ];

  return (
    <div className="bg-[#1a2744] shadow-2xl rounded-2xl overflow-hidden w-full max-w-4xl mx-auto font-arial">
      {/* Main Content Area */}
      <div className="p-4 sm:p-6 md:p-8">

        {/* Header Section with Tag Selector */}
        <div className="mb-4 sm:mb-6">
          {/* Top bar: company name + tag selector */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <Home className="h-5 w-5 sm:h-6 sm:w-6 text-[#f5a623] flex-shrink-0" />
              <span className="text-white text-xs sm:text-sm font-semibold uppercase tracking-wider truncate">
                {agentData?.company || "Premium Properties"}
              </span>
            </div>

            {/* Tag Selector */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Tag className="h-3 w-3 sm:h-4 sm:w-4 text-[#f5a623] flex-shrink-0" />
              {readOnly ? (
                <span className="bg-[#f5a623]/20 text-[#f5a623] px-3 py-1 rounded text-xs sm:text-sm font-semibold">
                  {selectedList || property?.propertyListType?.list_name || "Modern"}
                </span>
              ) : isCustomList ? (
                <div className="flex items-center gap-1 sm:gap-2">
                  <input
                    type="text"
                    value={customListName}
                    onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                    placeholder="Custom tag"
                    maxLength={20}
                    className="bg-[#f5a623]/20 text-[#f5a623] placeholder-[#f5a623]/60 px-2 sm:px-3 py-1 rounded text-xs sm:text-sm outline-none border border-[#f5a623]/30 w-28 sm:w-40"
                    autoFocus
                    disabled={isSavingCustomList}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isSavingCustomList) { e.preventDefault(); saveCustomList(); }
                      if (e.key === "Escape") cancelCustomList();
                    }}
                  />
                  <button
                    onClick={saveCustomList}
                    disabled={!customListName.trim() || isSavingCustomList}
                    className="p-1 sm:p-1.5 bg-[#f5a623]/20 rounded hover:bg-[#f5a623]/30 disabled:opacity-50"
                  >
                    {isSavingCustomList ? (
                      <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 text-[#f5a623] animate-spin" />
                    ) : (
                      <Check className="h-3 w-3 sm:h-4 sm:w-4 text-[#f5a623]" />
                    )}
                  </button>
                  <button
                    onClick={cancelCustomList}
                    disabled={isSavingCustomList}
                    className="p-1 sm:p-1.5 bg-[#f5a623]/20 rounded hover:bg-[#f5a623]/30"
                  >
                    <X className="h-3 w-3 sm:h-4 sm:w-4 text-[#f5a623]" />
                  </button>
                </div>
              ) : (
                <select
                  value={selectedList}
                  onChange={(e) => handleListSelection(e.target.value)}
                  className="bg-[#f5a623]/20 text-[#f5a623] px-2 sm:px-4 py-1 sm:py-1.5 rounded text-xs sm:text-sm font-semibold border-none outline-none cursor-pointer max-w-[120px] sm:max-w-none"
                >
                  {allLists.map((list) => (
                    <option key={list.id} value={list.list_name} className="text-gray-900 bg-white">
                      {list.list_name}
                    </option>
                  ))}
                  <option value="__custom__" className="text-gray-900 bg-white">
                    + Add Custom
                  </option>
                </select>
              )}
            </div>
          </div>

          {/* Title Section */}
          <div className="mb-3 sm:mb-4">
            <div className="w-full min-w-0">
              {readOnly ? (
                // readOnly: truncate to 1 line on mobile, 2 lines on sm, unclamped on md+
                <p
                  className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase leading-tight w-full
                    overflow-hidden
                    line-clamp-1 sm:line-clamp-2 md:line-clamp-none"
                  title={flyerData.headline || "LUXURY HOUSE"}
                >
                  {flyerData.headline || "LUXURY HOUSE"}
                </p>
              ) : (
                // edit mode: single-line input — truncates naturally via overflow hidden
                <input
                  type="text"
                  value={flyerData.headline || ""}
                  onChange={(e) => handleInputChange("headline", e.target.value)}
                  className="text-3xl sm:text-4xl md:text-5xl font-bold text-white bg-transparent border-none outline-none uppercase w-full min-w-0 overflow-hidden text-ellipsis"
                  placeholder="LUXURY HOUSE"
                  title={flyerData.headline || ""}
                />
              )}
            </div>
            <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#f5a623] uppercase block mt-1 sm:mt-2 truncate">
              FOR SALE
            </span>
          </div>

          {/* Description */}
          <div className="max-w-full sm:max-w-md">
            {readOnly ? (
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                {flyerData.description || "Get your dream home with a minimalist design and the best interior"}
              </p>
            ) : (
              <textarea
                value={flyerData.description || "Get your dream home with a minimalist design and the best interior"}
                onChange={(e) => handleInputChange("description", e.target.value)}
                className="text-gray-300 text-xs sm:text-sm leading-relaxed bg-transparent border-none outline-none resize-none w-full"
                rows={2}
                placeholder="Get your dream home with exceptional features..."
              />
            )}
          </div>
        </div>

        {/* Property Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
          {/* Bedrooms */}
          <div className="bg-[#253556] rounded-lg p-2 sm:p-3 md:p-4 text-center">
            <Bed className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-[#f5a623] mx-auto mb-1 sm:mb-2" />
            {readOnly ? (
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white text-center mb-0.5 sm:mb-1">
                {flyerData.bedrooms ?? 0}
              </p>
            ) : (
              <input
                type="number"
                min="0"
                value={flyerData.bedrooms ?? ""}
                onChange={(e) => handleInputChange("bedrooms", e.target.value ? parseInt(e.target.value) : null)}
                className="text-lg sm:text-xl md:text-2xl font-bold text-white w-full text-center bg-transparent border-none outline-none mb-0.5 sm:mb-1 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
                placeholder="0"
              />
            )}
            <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-400 uppercase">Bedrooms</p>
          </div>

          {/* Bathrooms */}
          <div className="bg-[#253556] rounded-lg p-2 sm:p-3 md:p-4 text-center">
            <Bath className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-[#f5a623] mx-auto mb-1 sm:mb-2" />
            {readOnly ? (
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white text-center mb-0.5 sm:mb-1">
                {flyerData.bathrooms ?? 0}
              </p>
            ) : (
              <input
                type="number"
                value={flyerData.bathrooms ?? ""}
                onChange={(e) => handleInputChange("bathrooms", e.target.value ? parseInt(e.target.value) : null)}
                className="text-lg sm:text-xl md:text-2xl font-bold text-white w-full text-center bg-transparent border-none outline-none mb-0.5 sm:mb-1 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
                placeholder="0"
              />
            )}
            <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-400 uppercase">Bathrooms</p>
          </div>

          {/* Sq Ft */}
          <div className="bg-[#253556] rounded-lg p-2 sm:p-3 md:p-4 text-center">
            <Square className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-[#f5a623] mx-auto mb-1 sm:mb-2" />
            {readOnly ? (
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-white text-center mb-0.5 sm:mb-1">
                {flyerData.sqft ?? flyerData.area ?? 0}
              </p>
            ) : (
              <input
                type="number"
                value={flyerData.sqft ?? flyerData.area ?? ""}
                onChange={(e) => handleInputChange("sqft", e.target.value ? parseInt(e.target.value) : null)}
                className="text-lg sm:text-xl md:text-2xl font-bold text-white w-full text-center bg-transparent border-none outline-none mb-0.5 sm:mb-1 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
                placeholder="0"
              />
            )}
            <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-400 uppercase">Sq Ft</p>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-white text-base sm:text-lg font-bold uppercase">Property Features</h3>
            {!readOnly && (
              <span className="text-xs text-gray-400">{flyerData.features?.length || 0}/10</span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
            {(flyerData.features || []).map((feature: FeatureItem, index: number) => {
              const IconComponent = featureIcons[index % featureIcons.length] || Home;
              return (
                <div
                  key={feature.id ?? index}
                  className="flex items-center gap-2 sm:gap-3 bg-[#253556] rounded-lg p-2 sm:p-3 group hover:bg-[#2d3f64] transition-colors min-w-0"
                >
                  <span className="text-[#f5a623] flex-shrink-0 text-sm">*</span>
                  {readOnly ? (
                    <span className="text-xs sm:text-sm text-white flex-1 truncate">
                      {feature.feature_name || "Feature"}
                    </span>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={feature.feature_name}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        onBlur={() => handleFeatureBlur(index)}
                        maxLength={30}
                        placeholder="Feature (max 30 chars)..."
                        className="bg-transparent border-none outline-none text-xs sm:text-sm text-white flex-1 min-w-0"
                      />
                      <button
                        onClick={() => handleFeatureDelete(index)}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 flex-shrink-0"
                      >
                        <X className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {!readOnly && (flyerData.features?.length || 0) < 10 && (
            <Button
              size="sm"
              variant="outline"
              onClick={addFeature}
              className="w-full text-xs border-[#f5a623]/30 text-[#f5a623] hover:bg-[#f5a623]/10"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Feature
            </Button>
          )}
        </div>

        {/* Images Grid — responsive: stack on mobile, 3-col on sm+ */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {/* Main Large Image — full width on mobile, 2 cols on sm+ */}
          <div className="sm:col-span-2 relative rounded-xl overflow-hidden h-48 sm:h-56 md:h-64 group">
            {isOpenHouseConfirmed && (
              <div className="absolute top-3 right-3 z-20">
                <div className="relative w-16 sm:w-20 md:w-24">
                  <img src={openHouseBadge} alt="Open House" className="w-full drop-shadow-xl" />
                  <span className="absolute inset-0 flex items-center justify-center text-white font-extrabold text-[9px] sm:text-[10px] md:text-xs tracking-wide text-center pointer-events-none">
                    OPEN<br />HOUSE
                  </span>
                </div>
              </div>
            )}

            {images[0] ? (
              <img src={images[0]} alt="Main property" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-[#253556] flex flex-col items-center justify-center">
                <ImageIcon className="h-10 w-10 sm:h-14 sm:w-14 md:h-16 md:w-16 text-gray-500 mb-2" />
                <p className="text-xs sm:text-sm text-gray-400">{readOnly ? "No image" : "Upload main photo"}</p>
              </div>
            )}
            {!readOnly && (
              <Button
                size="sm"
                variant="outline"
                className="absolute top-2 right-2 sm:top-3 sm:right-3 z-30 bg-white/90 hover:bg-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-3 w-3 mr-1" />
                Change
              </Button>
            )}
            {!readOnly && (
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleMainImageChange} />
            )}
          </div>

          {/* Secondary images — side-by-side row on mobile, stacked column on sm+ */}
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-3 sm:gap-4">
            {/* Photo 2 */}
            <div className="relative rounded-xl overflow-hidden h-28 sm:h-[calc(50%-8px)] md:h-[120px] group">
              {images[1] ? (
                <img src={images[1]} alt="Property view 2" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#253556] flex items-center justify-center">
                  <ImageIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500" />
                </div>
              )}
              {!readOnly && (
                <button
                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  onClick={() => secondaryImageRef.current?.click()}
                >
                  <Upload className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </button>
              )}
              {!readOnly && (
                <input
                  ref={secondaryImageRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleSecondaryImageChange(e, "photo2")}
                />
              )}
            </div>

            {/* Photo 3 */}
            <div className="relative rounded-xl overflow-hidden h-28 sm:h-[calc(50%-8px)] md:h-[120px] group">
              {images[2] ? (
                <img src={images[2]} alt="Property view 3" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#253556] flex items-center justify-center">
                  <ImageIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500" />
                </div>
              )}
              {!readOnly && (
                <button
                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  onClick={() => tertiaryImageRef.current?.click()}
                >
                  <Upload className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </button>
              )}
              {!readOnly && (
                <input
                  ref={tertiaryImageRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleSecondaryImageChange(e, "photo3")}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Orange Bottom Section with Agent Info */}
      <div className="bg-[#f5a623] px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6">
        {/* 
          Layout:
          - Mobile: stacked (agent info / price / address all full-width, vertically)
          - Tablet (sm): 2-col (agent left, price+address right)
          - Desktop (md+): 3-col original layout
        */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 items-start md:items-center">

          {/* Agent Info */}
          <div className="text-[#1a2744]">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center border-2 border-[#1a2744]/20 overflow-hidden flex-shrink-0">
                {agentData?.avatar ? (
                  <img src={agentData.avatar} alt="Agent" className="w-full h-full object-cover" />
                ) : (
                  <img src={profile} alt="Default" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full border border-[#1a2744]/20 shadow-sm overflow-hidden flex items-center justify-center flex-shrink-0">
                {agentData?.company_logo ? (
                  <img src={agentData.company_logo} alt="Company Logo" className="w-full h-full object-cover rounded-full" />
                ) : (
                  <span className="text-[7px] sm:text-[8px] text-gray-400 text-center px-1">Logo</span>
                )}
              </div>
            </div>

            {readOnly ? (
              <p className="text-xs sm:text-sm font-bold mb-1 truncate">{flyerData.agentName || "Agent Name"}</p>
            ) : (
              <input
                type="text"
                value={flyerData.agentName || ""}
                onChange={(e) => handleInputChange("agentName", e.target.value)}
                className="text-xs sm:text-sm font-bold bg-transparent border-none outline-none w-full mb-1"
                placeholder="Agent Name"
              />
            )}

            <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm mb-1 min-w-0">
              <Phone className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              {readOnly ? (
                <span className="truncate">{flyerData.agentPhone || "N/A"}</span>
              ) : (
                <input
                  type="text"
                  value={flyerData.agentPhone || ""}
                  onChange={(e) => handleInputChange("agentPhone", e.target.value)}
                  className="bg-transparent border-none outline-none flex-1 min-w-0 text-xs sm:text-sm"
                  placeholder="+123-456-7890"
                />
              )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm mb-1 min-w-0">
              <Mail className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              {readOnly ? (
                <span className="truncate">{flyerData.agentEmail || "N/A"}</span>
              ) : (
                <input
                  type="text"
                  value={flyerData.agentEmail || ""}
                  onChange={(e) => handleInputChange("agentEmail", e.target.value)}
                  className="bg-transparent border-none outline-none flex-1 min-w-0 text-xs sm:text-sm"
                  placeholder="agent@email.com"
                />
              )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm min-w-0">
              <Building2 className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              {readOnly ? (
                <span className="truncate">{flyerData.company || "N/A"}</span>
              ) : (
                <input
                  type="text"
                  value={flyerData.company || ""}
                  onChange={(e) => handleInputChange("company", e.target.value)}
                  className="bg-transparent border-none outline-none flex-1 min-w-0 text-xs sm:text-sm"
                  placeholder="XYZ Realty"
                />
              )}
            </div>
          </div>

          {/* Price & CTA — centered, full-width on mobile, right-col on sm, center-col on md */}
          <div className="text-center sm:text-right md:text-center">
            <p className="text-xs font-medium text-[#1a2744] mb-1 sm:mb-2 uppercase">Price</p>
            {readOnly ? (
              <p className="text-xl sm:text-2xl md:text-2xl font-extrabold text-[#1a2744] text-center sm:text-right md:text-center mb-2 sm:mb-3 break-all">
                {flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : "$0"}
              </p>
            ) : (
              <input
                type="text"
                value={flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : ""}
                onChange={(e) => {
                  const num = e.target.value.replace(/[^0-9]/g, "");
                  handleInputChange("price", num === "" ? null : Number(num));
                }}
                className="text-xl sm:text-2xl md:text-3xl font-extrabold text-[#1a2744] bg-transparent outline-none w-full text-center sm:text-right md:text-center mb-2 sm:mb-3"
                placeholder="$725,000"
              />
            )}
          </div>

          {/* Address — left on mobile/tablet, right-aligned on md */}
          <div className="text-[#1a2744] md:text-right">
            <div className="flex items-start sm:items-center justify-start md:justify-end gap-1.5 sm:gap-2">
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5 sm:mt-0" />
              {readOnly ? (
                <span className="text-xs sm:text-sm break-words">{flyerData.address || "No address"}</span>
              ) : (
                <input
                  type="text"
                  value={flyerData.address || ""}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className="bg-transparent border-none outline-none text-xs sm:text-sm md:text-right flex-1 min-w-0"
                  placeholder="Property Address"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template10;
